import React from "react";
import { Link } from "react-router-dom";

const COMPANY_NAME = "SSQUARE LOGIC";
const PRODUCT_NAME = "StayServe";

const sections = [
  {
    number: "01",
    title: "What Are Cookies?",
    content: (
      <>
        <p>
          Cookies are small text files that websites store on your device
          when you visit them. They help websites remember information,
          maintain sessions, understand how visitors use the website and
          improve the overall experience.
        </p>

        <p>
          {PRODUCT_NAME}, operated by {COMPANY_NAME}, may use cookies and
          similar technologies on its website and services.
        </p>
      </>
    ),
  },
  {
    number: "02",
    title: "How We Use Cookies",
    content: (
      <>
        <p>We may use cookies and similar technologies to:</p>

        <ul>
          <li>Keep the website and services functioning correctly.</li>
          <li>Maintain login and session-related functionality.</li>
          <li>Remember selected preferences and settings.</li>
          <li>Understand how visitors use our website.</li>
          <li>Improve website performance and user experience.</li>
          <li>Help protect our services against misuse and unauthorized activity.</li>
        </ul>
      </>
    ),
  },
  {
    number: "03",
    title: "Types of Cookies We May Use",
    content: (
      <>
        <h3 className="text-lg font-semibold text-stone-900">
          Essential Cookies
        </h3>

        <p>
          These cookies may be necessary for core website functionality,
          security, authentication, navigation or other essential services.
          Disabling these cookies may affect certain parts of the website.
        </p>

        <h3 className="mt-6 text-lg font-semibold text-stone-900">
          Preference Cookies
        </h3>

        <p>
          These cookies may remember choices such as preferences or settings
          so that the website can provide a more convenient experience.
        </p>

        <h3 className="mt-6 text-lg font-semibold text-stone-900">
          Analytics Cookies
        </h3>

        <p>
          Where analytics services are used, cookies or similar technologies
          may help us understand website traffic, usage patterns and
          performance.
        </p>

        <h3 className="mt-6 text-lg font-semibold text-stone-900">
          Security Cookies
        </h3>

        <p>
          Certain cookies or similar technologies may be used to detect
          suspicious activity, protect accounts and maintain the security of
          our services.
        </p>
      </>
    ),
  },
  {
    number: "04",
    title: "Third-Party Cookies",
    content: (
      <>
        <p>
          Some features of our website may involve third-party services.
          Depending on the services enabled, those providers may use their own
          cookies or similar technologies.
        </p>

        <p>
          Examples may include payment providers, analytics services, embedded
          maps, security services or other integrations.
        </p>

        <p>
          Third-party cookies are governed by the applicable third party's
          policies and practices.
        </p>
      </>
    ),
  },
  {
    number: "05",
    title: "Payment and Security Services",
    content: (
      <>
        <p>
          Payments for {PRODUCT_NAME} may be processed through third-party
          payment providers such as Razorpay or supported direct UPI
          mechanisms.
        </p>

        <p>
          Payment providers may use cookies or similar technologies as part of
          their payment, security, fraud-prevention or authentication
          processes.
        </p>

        <p>
          We do not use cookies to intentionally collect complete card numbers,
          CVV information or other sensitive payment credentials for our own
          purposes.
        </p>
      </>
    ),
  },
  {
    number: "06",
    title: "How Long Cookies Stay on Your Device",
    content: (
      <>
        <p>
          Some cookies are session cookies and are removed when you close your
          browser. Other cookies may remain on your device for a longer period
          until they expire or are deleted by you.
        </p>

        <p>
          The duration of a cookie depends on its purpose and the service that
          places it.
        </p>
      </>
    ),
  },
  {
    number: "07",
    title: "Managing Cookies",
    content: (
      <>
        <p>
          Most modern browsers allow you to view, block, delete or otherwise
          manage cookies through their settings.
        </p>

        <p>
          You can choose to disable cookies, but doing so may affect the
          availability or functionality of certain features of the website.
        </p>
      </>
    ),
  },
  {
    number: "08",
    title: "Do Not Track and Similar Signals",
    content: (
      <p>
        Browser and device settings may provide certain privacy preferences
        or signals. Where technically applicable, we may consider such
        settings in accordance with our services, available technology and
        applicable requirements.
      </p>
    ),
  },
  {
    number: "09",
    title: "Changes to This Cookie Policy",
    content: (
      <p>
        We may update this Cookie Policy from time to time to reflect changes
        in our website, services, technologies, third-party integrations or
        applicable requirements. Any updated version will be published on this
        page.
      </p>
    ),
  },
  {
    number: "10",
    title: "Contact Us",
    content: (
      <>
        <p>
          If you have questions about our use of cookies or similar
          technologies, please contact {COMPANY_NAME} using the contact
          details provided on our website.
        </p>

        <Link
          to="/#contact"
          className="mt-4 inline-flex rounded-xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          Contact {COMPANY_NAME} →
        </Link>
      </>
    ),
  },
];

export default function CookiePolicy() {
  return (
    <main className="min-h-screen bg-stone-50 text-stone-900">
      {/* Header */}
      <section className="border-b border-stone-200 bg-stone-950 text-white">
        <div className="mx-auto max-w-6xl px-6 py-20 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-orange-400 transition hover:text-orange-300"
          >
            ← Back to {PRODUCT_NAME}
          </Link>

          <div className="mt-10 max-w-4xl">
            <div className="mb-5 inline-flex rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-orange-400">
              Legal
            </div>

            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Cookie Policy
            </h1>

            <p className="mt-6 max-w-3xl text-lg leading-8 text-stone-300">
              This Cookie Policy explains how {PRODUCT_NAME}, operated by{" "}
              {COMPANY_NAME}, may use cookies and similar technologies on its
              website and services.
            </p>

            <p className="mt-6 text-sm text-stone-500">
              Effective Date: 18 August 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="mx-auto max-w-5xl px-6 py-16 lg:px-8">
        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm sm:p-10">
          <div className="space-y-12">
            {sections.map((section) => (
              <section
                key={section.number}
                className="border-b border-stone-200 pb-10 last:border-b-0 last:pb-0"
              >
                <div className="flex gap-5">
                  <div className="hidden shrink-0 text-sm font-bold text-orange-500 sm:block">
                    {section.number}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold tracking-tight text-stone-950">
                      {section.title}
                    </h2>

                    <div className="mt-5 space-y-4 text-[15px] leading-7 text-stone-600">
                      {section.content}
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-5xl px-6 pb-16 lg:px-8">
        <div className="rounded-3xl bg-stone-950 px-8 py-12 text-center text-white">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-400">
            {COMPANY_NAME}
          </p>

          <h2 className="mt-3 text-3xl font-bold">
            Questions about cookies?
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-stone-400">
            Contact our team if you have questions about cookies, privacy or
            how our website uses similar technologies.
          </p>

          <Link
            to="/#contact"
            className="mt-7 inline-flex rounded-xl bg-orange-500 px-6 py-3 font-semibold text-white transition hover:bg-orange-600"
          >
            Contact Us →
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-8 text-sm text-stone-500 sm:flex-row sm:items-center sm:justify-between lg:px-8">
          <p>
            © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
          </p>

          <div className="flex flex-wrap gap-5">
            <Link
              to="/privacy-policy"
              className="transition hover:text-orange-500"
            >
              Privacy Policy
            </Link>

            <Link
              to="/terms-and-conditions"
              className="transition hover:text-orange-500"
            >
              Terms & Conditions
            </Link>

            <Link
              to="/refund-policy"
              className="transition hover:text-orange-500"
            >
              Refund Policy
            </Link>

            <Link
              to="/cookie-policy"
              className="text-orange-500"
            >
              Cookie Policy
            </Link>
          </div>
        </div>
      </footer>
    </main>
  );
}